import { Fragment, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { EmptyCell, EmptyState, type IColumn, Table, Tooltip, Typography } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { ChatbotInstructionType, type ChatbotListItem } from 'entities/Chatbot'
import { ChatbotRoutes } from 'pages/chatbot'
import { useChatbotListContext } from 'pages/chatbot/context'
import { ChatbotStatusBadge } from 'pages/chatbot/ui/ChatbotStatus'
import { TextingActiveToggle } from 'pages/chatbot/ui/TextingActiveToggle'

import styles from './styles.module.scss'
import { type IChatbotRow, useRowsWithActions } from '../hooks/useRowsWithActions'

type IUpdateStatus = (id: number, isActive: boolean) => Promise<void>

const NameCell = observer(
  ({ payload, updateStatus }: { payload: ChatbotListItem; updateStatus: IUpdateStatus }) => (
    <div className={styles.nameCellWrapper}>
      <Typography
        variant={'body-md-regular'}
        ellipsis
        tooltipProps={{
          label: payload.name,
        }}
        ariaLabel={'name'}
        className={styles.nameText}
      >
        {payload.name}
      </Typography>
      <TextingActiveToggle
        item={payload}
        onToggle={() => updateStatus(payload.id, !payload.isActive)}
        className={styles.nameToggle}
      />
    </div>
  )
)

const getColumns = (updateStatus: IUpdateStatus): IColumn<IChatbotRow>[] => [
  {
    field: 'name',
    name: 'Name',
    width: '100%',
    minWidth: '260px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => <NameCell payload={payload} updateStatus={updateStatus} />,
  },
  {
    field: 'status',
    name: 'Status',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => (
      <Tooltip label={payload.isInvalid && 'Suspicious content detected'} placement={'top'}>
        <ChatbotStatusBadge status={payload.status} />
      </Tooltip>
    ),
  },
  {
    field: 'totalContacts',
    name: 'Started',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => numberFormat({ value: payload.totalContacts }),
  },
  {
    field: 'fallbackContacts',
    name: 'Abandoned',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => {
      return payload.type === ChatbotInstructionType.Unified ? (
        <EmptyCell />
      ) : (
        numberFormat({ value: payload.fallbackContacts })
      )
    },
  },
  {
    field: 'completedContacts',
    name: 'Completed',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => numberFormat({ value: payload.completedContacts }),
  },
  {
    field: 'terminatedContacts',
    name: 'Stopped by user',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ payload }) => numberFormat({ value: payload.terminatedContacts }),
  },
]

export const TextingTable = observer(() => {
  const navigate = useNavigate()
  const { page, limit, total, noData, loading, loadingScheduled, changePaging, updateStatus } =
    useChatbotListContext()

  const rows = useRowsWithActions()
  const columns = useMemo(() => getColumns(updateStatus), [updateStatus])

  if (noData) return <EmptyState text='No results found' />

  return (
    <section className={styles.table}>
      <Table
        ariaLabel={'ChatbotListTable'}
        columns={columns}
        rows={rows}
        pinnedColumnNames={['name']}
        initPage={page}
        limit={limit}
        rightHeaderContent={<Fragment />}
        rowsCount={total}
        onChangePagination={changePaging}
        startPaginationSize={10}
        isLoading={loading || loadingScheduled}
        onRowClick={({ id }) => navigate(`/${ChatbotRoutes.root}/${id}/${ChatbotRoutes.analytic}`)}
      />
    </section>
  )
})
